import * as z from 'zod';
import {
  listResponseMetaSchema,
  measurementTypeSchema,
  productModerationStateSchema,
  optional,
  productStatusSchema,
  productUnavailableReasonSchema,
  timestampSchema,
} from './common';

// Product Service — `v1Product` with its modifier groups/options, plus the
// per-restaurant overlay (`v1RestaurantProduct`) that carries branch-local
// price/stock/status. Money fields are integer so'm.

// `v1SelectionType` existed from v1.00.180 to v1.00.227 and is now DELETED
// from the spec: the independent `min_select`/`max_select` pair it replaced is
// back on `v1ModifierGroup` (providers send real ranges — min == max == 8 —
// which the two-value enum could not express). Derive the old vocabulary:
// `max_select == 1` → single (radio), anything else → multi;
// `max_select` unset → no ceiling; `min_select == 0` → the group is optional.

export const modifierOptionSchema = z.object({
  id: z.string(),
  external_id: z.string().default(''),
  name: z.string().default(''),
  /**
   * UZS so'm ADDED to the product's price. Renamed from `price_delta` in
   * v1.00.180 — the old key no longer exists anywhere in the spec, and the
   * gateway rejects unknown fields, so sending it fails the whole write.
   */
  price: z.number().int().default(0),
  min_amount: z.number().int().nonnegative().default(0),
  max_amount: z.number().int().nonnegative().default(0),
  default_selected: z.boolean().default(false),
  sort_order: z.number().int().default(0),
  /**
   * OFD fiscal classifiers, per OPTION (v1.00.180). An option can be a
   * separately-fiscalized good — a topping sold by weight has its own IKPU —
   * so these mirror the product's own codes rather than inheriting them.
   */
  ikpu_code: z.string().default(''),
  unit_code: z.string().default(''),
  has_marked_code: z.boolean().default(false),
  marked_code: z.string().default(''),
  /** Same inherit-vs-zero semantics as the product's `vat_percent`. */
  vat_percent: z.number().nullish(),
  /**
   * Availability, folded down for the storefront: stock, the merchant's
   * stop-list switch and the provider's stop-list in ONE flag (v1.00.227).
   * On CUSTOMER reads it is the only availability signal — `stock_quantity`,
   * `status` and the fiscal codes no longer reach the storefront; merchant
   * reads keep the raw fields below alongside it.
   */
  is_unavailable: z.boolean().default(false),
  /**
   * Restaurant-scoped state (meaningful on the by-restaurant merchant reads —
   * inert on the unscoped merchant/admin reads, which still carry the keys).
   */
  stock_quantity: z.number().int().default(0),
  status: productStatusSchema.optional(),
});
export type ModifierOption = z.infer<typeof modifierOptionSchema>;

export const modifierGroupSchema = z.object({
  id: z.string(),
  external_id: z.string().default(''),
  name: z.string().default(''),
  sort_order: z.number().int().default(0),
  /**
   * How many of this group's options a customer MUST pick. `0` (the wire's
   * omitted zero value) means the group is optional. Back on the wire as of
   * v1.00.227 — `selection_type`, which replaced the pair in v1.00.180, is
   * deleted again; see the note above `modifierOptionSchema`.
   */
  min_select: z.number().int().nonnegative().default(0),
  /**
   * How many a customer MAY pick. `0`/absent means NO ceiling; `1` is the old
   * `SINGLE` (a radio group). Not to be confused with `max_options` below,
   * which bounds the option LIST, not the customer's choice.
   */
  max_select: z.number().int().nonnegative().default(0),
  /**
   * How many options the GROUP may HOLD — a merchant-set ceiling (1..50) on the
   * size of the option list, not on how many a customer may choose. UNSET means
   * "no group-specific cap" and the system's own limit of 50 applies; as of
   * v1.00.184 a literal `0` is never valid (a group must always hold at least
   * one option), so on a READ the defaulted 0 simply means "no cap", and a
   * WRITE must omit the field rather than send 0.
   *
   * Easy to misread as `max_select` above. It is not: this bounds what the
   * MERCHANT may put in the group, not what the customer may choose.
   */
  max_options: z.number().int().nonnegative().default(0),
  options: z.array(modifierOptionSchema).default([]),
});
export type ModifierGroup = z.infer<typeof modifierGroupSchema>;

/**
 * `v1LocalizedText` — one field's value in every language the platform speaks.
 *
 * `ru` is canonical: a request with no (or an unsupported) `x-locale` falls
 * back to it, and admin listings sort by it. `uz`/`en` are optional, and an
 * EMPTY STRING means "not translated yet" — never "translated to empty" — so
 * blank members are dropped rather than sent (the translations table refuses
 * to store an empty value).
 */
export const localizedTextSchema = z.object({
  ru: z.string().default(''),
  uz: z.string().default(''),
  en: z.string().default(''),
});
export type LocalizedText = z.infer<typeof localizedTextSchema>;

/**
 * `v1LocalizedModifierOption` — a modifier option whose `name` carries every
 * stored locale instead of one resolved string. Populated only where
 * `LocalizedProduct` itself is (the merchant get-by-id), and numbered
 * independently of `ModifierOption` on the wire.
 */
export const localizedModifierOptionSchema = modifierOptionSchema.extend({
  name: localizedTextSchema.default({ ru: '', uz: '', en: '' }),
});
export type LocalizedModifierOption = z.infer<typeof localizedModifierOptionSchema>;

/** `v1LocalizedModifierGroup` — the group-level counterpart of the above. */
export const localizedModifierGroupSchema = modifierGroupSchema.extend({
  name: localizedTextSchema.default({ ru: '', uz: '', en: '' }),
  options: z.array(localizedModifierOptionSchema).default([]),
});
export type LocalizedModifierGroup = z.infer<typeof localizedModifierGroupSchema>;

/**
 * `v1ProductImage` — one gallery photo, as of API v1.00.141. Images became
 * FIRST-CLASS OBJECTS in that release (each with its own id, ordering and
 * cover flag, managed via `POST/PUT/DELETE products/{id}/images*`); before it
 * `images` was a bare `string[]` of URLs. Parsing the old shape against the
 * new wire is exactly how the Menu screen broke: one object where a string was
 * expected fails the whole list envelope, and the resulting Zod error carries
 * no HTTP status, so the query retried it as if the network had hiccuped.
 *
 * `id` is what the reorder endpoint and the Image Service address; `url` is the
 * expanded display URL (reads still expand storage keys one-way, see the
 * app's CLAUDE.md #13). Zero-valued members are omitted on the wire
 * (protobuf-JSON), hence the defaults.
 *
 * The cover is named by `image_type` as of the 2026-08-14 revision — the
 * boolean `is_main` it replaced is gone from the wire entirely. The values are
 * LOWERCASE here (`'main'`/`'common'`), mirroring `image.v1.ImageType`'s
 * product members without sharing its uppercase spelling; the write side sends
 * the uppercase {@link IMAGE_TYPES}. A row from before the rename parses as
 * `''`, which {@link coverImageUrl} treats as "not the cover" rather than
 * throwing.
 */
export const productImageSchema = z.object({
  id: z.string().default(''),
  url: z.string().default(''),
  position: z.number().int().default(0),
  image_type: z.string().default(''),
});
export type ProductImage = z.infer<typeof productImageSchema>;

/** `image_type`'s cover value on a READ row — lowercase, unlike the write enum. */
export const PRODUCT_IMAGE_MAIN = 'main';

/**
 * A product gallery in the merchant's chosen order — `position` ascending, since
 * the wire does not promise sorted rows. Entries with no `url` are dropped:
 * protobuf-JSON omits zero values, so a half-built row parses as `''`.
 */
export function orderedImages(images: ProductImage[]): ProductImage[] {
  return [...images]
    .filter((entry) => entry.url !== '')
    .sort((a, b) => a.position - b.position);
}

/**
 * A product's cover photo URL: the `main`-typed entry, else the first by position.
 * `undefined` — not `''` — when there is no photo, so callers can spread it away.
 *
 * This is the ONLY way to a cover as of API v1.00.155: the legacy top-level
 * `image` string was dropped from every product shape when images became
 * first-class objects, so anything still reading `product.image` is reading a
 * field the wire no longer sends.
 */
export function coverImageUrl(images: ProductImage[]): string | undefined {
  const gallery = orderedImages(images);
  return (gallery.find((entry) => entry.image_type === PRODUCT_IMAGE_MAIN) ?? gallery[0])
    ?.url;
}

export const productSchema = z.object({
  id: z.string(),
  /** The menu section this product belongs to (Product Category Service). */
  product_category_id: z.string().default(''),
  name: z.string().default(''),
  description: z.string().default(''),
  composition: z.string().nullish(),
  nutrition: z
    .object({
      calories: z.number().int().nullish(),
      fats: z.number().nullish(),
      proteins: z.number().nullish(),
    })
    .nullish(),
  /** Base price in so'm; a branch may override it via `RestaurantProduct`. */
  price: z.number().int().nonnegative().default(0),
  status: productStatusSchema.optional(),
  measurement_type: measurementTypeSchema.optional(),
  cook_time_seconds: z.number().int().nonnegative().default(0),
  created_at: optional(timestampSchema),
  updated_at: optional(timestampSchema),
  like_count: z.number().int().nonnegative().default(0),
  is_liked: z.boolean().default(false),
  external_id: z.string().default(''),
  /** Price after any applied promotion; equals `price` when none applies. */
  effective_price: z.number().int().nonnegative().default(0),
  /** Set when a promotion is currently discounting this product. */
  applied_promotion_id: z.string().default(''),
  /** Fields the merchant has overridden away from the imported menu source. */
  overridden_fields: z.array(z.string()).default([]),
  images: z.array(productImageSchema).default([]),
  modifiers: z.array(modifierGroupSchema).default([]),
  is_always_ready: z.boolean().default(false),
  /** 18+ product (alcohol, tobacco…): the storefront age-gates and masks it. */
  is_adult: z.boolean().default(false),
  /**
   * The dish's modifiers are switched off wholesale — one switch for the whole
   * dish, at every branch. The groups are KEPT (merchant shapes still carry
   * them, so the editor can draw what it would bring back); the storefront
   * sells the dish plain and rejects an order naming an option. Written only
   * through `PUT /merchant/products/modifiers-disabled`, never the PATCH.
   * Defaults false because protobuf-JSON omits a false bool.
   */
  modifiers_disabled: z.boolean().default(false),
  stock_quantity: z.number().int().default(0),
  /**
   * Selling hours set on the dish itself, "08:00". Empty = no limit on that
   * side, i.e. sold all day. protobuf-JSON omits the empty string.
   */
  available_from: optional(z.string()),
  /** End of the selling hours, "11:00"; empty = sold all day. */
  available_to: optional(z.string()),
  /**
   * Client reads only: why the dish can't be ordered right now. `UNSPECIFIED`
   * (the default) means it can. See {@link productUnavailableReasonSchema}.
   */
  unavailable_reason: productUnavailableReasonSchema.optional(),
});
export type Product = z.infer<typeof productSchema>;

/**
 * OFD fiscalization fields, carried by the MERCHANT-facing product shapes only.
 *
 * `v1Product` is also the public client-facing type, and the spec is explicit
 * that these must never be added there — a storefront must not see a product's
 * tax classifiers. So they live on `MerchantProduct`/`LocalizedProduct`, which
 * only the merchant/admin RPCs answer with.
 */
const fiscalFields = {
  /** IKPU/SPIC classifier code, required by OFD fiscalization. */
  ikpu_code: z.string().default(''),
  /** The product's category is subject to mandatory goods marking. */
  has_marked_code: z.boolean().default(false),
  /** Marking (Data Matrix) code; set only when `has_marked_code` is true. */
  marked_code: z.string().default(''),
  /**
   * VAT rate in %. Absent/null = the product inherits the brand's rate; `0` is
   * an explicit "no VAT". Presence matters, so there is deliberately no default.
   */
  vat_percent: z.number().nullish(),
  /** OFD measurement-unit classifier code (Soliq tasnif). */
  unit_code: z.string().default(''),
  /** Net amount of one unit, in the unit `unit_code` names; 0 = unknown. */
  measure_amount: z.number().nonnegative().optional(),
  /**
   * The BRANCH's own selling hours, on branch-scoped merchant reads. Empty =
   * the branch set none and inherits the dish's `available_from/to` — exactly
   * like `own_price`. Distinguishes "branch override" from "inherited" so the
   * editor doesn't write the inherited value back as if it were the branch's.
   */
  own_available_from: optional(z.string()),
  own_available_to: optional(z.string()),
};

/**
 * `v1MerchantProduct` — what every MERCHANT read RPC answers with as of API
 * v1.00.155 (`products/by-merchant`, `.../by-restaurant`, `products/top`,
 * `products/{id}/by-merchant`, and reset-overrides).
 *
 * It is `Product` with one locale resolved, PLUS the fiscal fields above. The
 * split exists because the merchant panel must see and edit those codes while
 * the public catalog must never receive them — the spec keeps the three
 * messages in sync by hand and warns against merging them.
 */
export const merchantProductSchema = productSchema.extend({
  ...fiscalFields,
  /**
   * Where the product stands in the shared MODERATION lifecycle — distinct
   * from `status` above, which is the branch's stop-list switch. A free-form
   * string on the wire (unlike `v1RestaurantLifecycleStatus`, which is a real
   * enum), so it is parsed as one and interpreted at the view-model boundary;
   * see `toProductModeration`.
   */
  moderation_state: productModerationStateSchema.optional(),
  /** The moderator's note when `moderation_state` is REJECTED. */
  rejection_reason: z.string().optional(),
});
export type MerchantProduct = z.infer<typeof merchantProductSchema>;

/**
 * `v1LocalizedProduct` — the same field set as `Product`, but with `name` and
 * `description` exposed as EVERY language at once instead of one locale-resolved
 * string. The merchant write RPCs (create/update) answer with this shape,
 * because an editor has to round-trip all three translations; the read RPCs
 * (list/get) still answer with locale-resolved `Product`.
 */
export const localizedProductSchema = merchantProductSchema.extend({
  name: localizedTextSchema.default({ ru: '', uz: '', en: '' }),
  description: localizedTextSchema.default({ ru: '', uz: '', en: '' }),
  composition: localizedTextSchema.nullish(),
  /**
   * Every modifier group's/option's name in ALL stored locales — the same
   * all-locale shape as this message's own `name`/`description`.
   *
   * v1.00.184 folded the short-lived side-by-side pair back into one field:
   * v1.00.180 had added `localized_modifiers` NEXT TO a locale-resolved
   * `modifiers`, and 184 deleted `localized_modifiers` while retyping
   * `modifiers` itself to `v1LocalizedModifierGroup`. Parsing this field with
   * the resolved group schema now fails — `name` arrives as an object.
   */
  modifiers: z.array(localizedModifierGroupSchema).default([]),
});
export type LocalizedProduct = z.infer<typeof localizedProductSchema>;

/**
 * `productV1PendingChangeRequest` — returned INSTEAD of the written entity when
 * a gated write was routed through moderation's queue rather than applied
 * directly. New in API v1.00.155, and it is why the write responses below have
 * their entity optional: a moderated merchant gets a receipt, not a product.
 */
export const pendingChangeRequestSchema = z.object({
  id: z.string().default(''),
  /** Always `"pending"` for a fresh submission. */
  status: z.string().default(''),
  submitted_at: optional(timestampSchema),
});
export type PendingChangeRequest = z.infer<typeof pendingChangeRequestSchema>;

/** Single-resource payload — `data` on the merchant get (one locale + fiscal). */
export const merchantProductResponseSchema = z.object({
  product: merchantProductSchema,
});

/**
 * Single-resource payload for the localized merchant READS
 * (`products/{id}/by-merchant[/by-restaurant]`), which answer with every
 * translation at once.
 *
 * `product` is REQUIRED here, unlike the write envelope below: a read is never
 * gated through moderation, so an absent product is genuine contract drift and
 * should fail at the boundary rather than surface as an empty editor.
 */
export const localizedProductRequiredResponseSchema = z.object({
  product: localizedProductSchema,
});

/**
 * Single-resource payload for published product read (`GET /products/{id}/published`).
 * Returned as `{ client_visible: true, product }` or `{ client_visible: false, product: null }`.
 */
export const publishedProductCardResponseSchema = z.object({
  client_visible: z.boolean().default(true),
  product: localizedProductSchema.nullable().optional(),
});
export type PublishedProductCardResponse = z.infer<
  typeof publishedProductCardResponseSchema
>;

/**
 * Single-resource payload — `data` on create/update (all translations).
 *
 * `product` is OPTIONAL as of v1.00.155: a gated write answers with only
 * `pending_change`, so a required `product` would throw on a request that
 * actually succeeded. Callers must handle both branches.
 */
export const localizedProductResponseSchema = z.object({
  product: localizedProductSchema.optional(),
  pending_change: pendingChangeRequestSchema.optional(),
});
export type ProductWriteResult = z.infer<typeof localizedProductResponseSchema>;

/**
 * `v1CreateModifierGroupResponse` / `v1UpdateModifierGroupResponse` /
 * `v1AttachModifierGroupResponse` — the group comes back LOCALIZED (all three
 * languages), matching what the write bodies carry.
 *
 * `group` is OPTIONAL for the same reason a product write's entity is: these
 * writes are moderation-gated, and a queued one answers with only
 * `pending_change`.
 */
export const modifierGroupResponseSchema = z.object({
  group: localizedModifierGroupSchema.optional(),
  pending_change: pendingChangeRequestSchema.optional(),
});
export type ModifierGroupWriteResult = z.infer<typeof modifierGroupResponseSchema>;

/** `v1CreateModifierOptionResponse` / `v1UpdateModifierOptionResponse`. */
export const modifierOptionResponseSchema = z.object({
  option: localizedModifierOptionSchema.optional(),
  pending_change: pendingChangeRequestSchema.optional(),
});
export type ModifierOptionWriteResult = z.infer<typeof modifierOptionResponseSchema>;

/**
 * `v1GetModifierGroupResponse` — the by-id read of a catalogue group. `group`
 * is REQUIRED: a read is never gated, so its absence is contract drift and
 * should fail at the boundary.
 */
export const modifierGroupRequiredResponseSchema = z.object({
  group: localizedModifierGroupSchema,
});

/**
 * `v1DeleteModifierGroupResponse` / `v1DetachModifierGroupResponse` /
 * `v1DeleteModifierOptionResponse` — no entity, only the moderation receipt
 * when the delete was queued rather than applied. Parsed (not fire-and-forget)
 * so a gated delete is never reported as done — the same trap the promotion
 * delete documents.
 */
export const modifierDeleteResponseSchema = z.object({
  pending_change: pendingChangeRequestSchema.optional(),
});
export type ModifierDeleteResult = z.infer<typeof modifierDeleteResponseSchema>;

/**
 * `v1RestaurantProduct` — one branch's overlay on a shared product: its local
 * price, stock and availability.
 */
export const restaurantProductSchema = z.object({
  restaurant_id: z.string(),
  product_id: z.string(),
  price: z.number().int().nonnegative().default(0),
  stock_quantity: z.number().int().default(0),
  status: productStatusSchema.optional(),
  /** The branch's own selling hours; empty = inherited from the dish. */
  own_available_from: optional(z.string()),
  own_available_to: optional(z.string()),
});
export type RestaurantProduct = z.infer<typeof restaurantProductSchema>;

/**
 * `POST /products` answers with a SPLIT envelope: the created product (all
 * translations) under `data.product`, and one overlay per branch it was
 * stocked in under the sibling top-level `list`. Both halves matter — the
 * product carries the server-minted id, the overlays carry each branch's
 * effective price — so the raw envelope is parsed rather than `unwrapData`d.
 */
export const createProductResponseSchema = z.object({
  data: localizedProductResponseSchema,
  list: z.array(restaurantProductSchema).default([]),
});
export type CreateProductResult = z.infer<typeof createProductResponseSchema>;

/**
 * `POST /products/{product_id}/restaurant-products` response envelope.
 * When adding overlays without price or on pre-launch branch: returns `list` with attached overlays.
 * When adding overlay with price on a live branch: returns `pending_change` and empty `list: []`.
 */
export const restaurantProductsResponseSchema = z.object({
  list: z.array(restaurantProductSchema).default([]),
  meta: listResponseMetaSchema.default({ total: 0 }),
  pending_change: pendingChangeRequestSchema.optional(),
});
export type RestaurantProductsResult = z.infer<typeof restaurantProductsResponseSchema>;

/** A product card returned by the merchant product-recommendations endpoints. */
export const recommendedProductSchema = z.object({
  product_id: z.string(),
  name: z.string().default(''),
  image: z.string().default(''),
  price: z.number().int().default(0),
  effective_price: z.number().int().default(0),
  measure_amount: z.number().default(0),
  measurement_type: measurementTypeSchema.default('MEASUREMENT_TYPE_UNSPECIFIED'),
  status: productStatusSchema.optional(),
  unavailable_reason: productUnavailableReasonSchema.nullish(),
  requires_selection: z.boolean().default(false),
});
export type RecommendedProduct = z.infer<typeof recommendedProductSchema>;

export const recommendationsResponseSchema = z.object({
  list: z.array(recommendedProductSchema).default([]),
  meta: listResponseMetaSchema.default({ total: 0 }),
});
export type RecommendationsResult = z.infer<typeof recommendationsResponseSchema>;

// The add/update/delete-image response schemas used to live here. Their
// endpoints are gone (see the note in `api/product-api.ts`); the Image Service
// answers with `upsertImageItemResultSchema` / `imageMutationResponseSchema`
// in `model/image.ts` instead.

/**
 * `PUT /merchant/products/modifiers-disabled` answer. `switched_product_ids` is
 * what actually flipped (a dish already in the requested state is not in it);
 * `skipped_product_ids` names ids that no longer exist — a dish deleted while
 * the list was open.
 */
export const productsModifiersDisabledResponseSchema = z.object({
  switched_product_ids: z.array(z.string()).default([]),
  skipped_product_ids: z.array(z.string()).default([]),
});
export type ProductsModifiersDisabledResult = z.infer<
  typeof productsModifiersDisabledResponseSchema
>;
